import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "@/App";
import {
  ArrowLeft, TrendingUp, ShoppingBag, Store, Percent,
  ArrowDown, ExternalLink, Loader2, Receipt, BarChart3,
  PiggyBank, MapPin, Trophy
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { motion } from "framer-motion";

const CATEGORY_EMOJIS = {
  fruits_legumes: "🥬",
  viande: "🥩",
  poisson: "🐟",
  produits_laitiers: "🥛",
  epicerie: "🛒",
  boissons: "🥤",
  surgeles: "🧊",
  hygiene: "🧴",
  autre: "📦"
};

const PurchaseStatsPage = () => {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [activeTab, setActiveTab] = useState("products");

  useEffect(() => {
    fetchStats();
  }, [token]);

  const fetchStats = async () => {
    try {
      const res = await axios.get(`${API}/receipts/stats/detailed`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(res.data);
    } catch (error) {
      toast.error("Erreur lors du chargement des statistiques");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!stats || stats.receipts_count === 0) {
    return (
      <div className="min-h-screen pb-24">
        <div className="bg-gradient-to-br from-blue-500/10 via-indigo-500/5 to-purple-500/10 px-4 pt-6 pb-8">
          <div className="max-w-4xl mx-auto">
            <Link to="/profile" className="inline-flex items-center gap-2 text-charcoal/70 hover:text-charcoal mb-4">
              <ArrowLeft className="w-4 h-4" />
              Retour
            </Link>
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center">
                <BarChart3 className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="font-heading text-2xl font-bold text-charcoal">Statistiques d'achats</h1>
                <p className="text-muted-foreground text-sm">Analysez vos dépenses</p>
              </div>
            </div>
          </div>
        </div>
        <div className="px-4 py-12 text-center">
          <Receipt className="w-16 h-16 text-muted-foreground/30 mx-auto mb-4" />
          <p className="text-muted-foreground">Aucun ticket scanné pour le moment</p>
          <Link to="/receipts">
            <Button className="mt-4 rounded-full">Scanner un ticket</Button>
          </Link>
        </div>
      </div>
    );
  }

  // Calculate potential total savings
  const totalPotentialSavings = stats.products_ranking
    .filter(p => p.cheaper_elsewhere)
    .reduce((sum, p) => sum + (p.cheaper_elsewhere.potential_savings * p.quantity_bought), 0);

  return (
    <div className="min-h-screen pb-24">
      {/* Header */}
      <div className="bg-gradient-to-br from-blue-500/10 via-indigo-500/5 to-purple-500/10 px-4 pt-6 pb-8">
        <div className="max-w-4xl mx-auto">
          <Link to="/profile" className="inline-flex items-center gap-2 text-charcoal/70 hover:text-charcoal mb-4">
            <ArrowLeft className="w-4 h-4" />
            Retour
          </Link>
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center">
              <BarChart3 className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="font-heading text-2xl font-bold text-charcoal">Statistiques d'achats</h1>
              <p className="text-muted-foreground text-sm">Analysez vos dépenses et économisez</p>
            </div>
          </div>
        </div>
      </div>

      <div className="px-4 -mt-4 max-w-4xl mx-auto space-y-4">
        {/* Summary Cards */}
        <div className="grid grid-cols-2 gap-3">
          <Card className="bg-gradient-to-br from-green-50 to-emerald-50 border-green-100">
            <CardContent className="p-4">
              <p className="text-xs text-green-600 font-medium">Total dépensé</p>
              <p className="text-2xl font-bold text-green-700">{stats.total_spent.toFixed(2)}€</p>
              <p className="text-xs text-green-600/70 mt-1">{stats.receipts_count} tickets</p>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-100">
            <CardContent className="p-4">
              <p className="text-xs text-blue-600 font-medium">Ce mois-ci</p>
              <p className="text-2xl font-bold text-blue-700">{stats.this_month_spent.toFixed(2)}€</p>
              <p className="text-xs text-blue-600/70 mt-1">Panier moyen: {stats.average_ticket.toFixed(2)}€</p>
            </CardContent>
          </Card>
        </div>

        {/* Potential Savings Alert */}
        {totalPotentialSavings > 1 && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <Card className="bg-gradient-to-r from-amber-50 to-orange-50 border-amber-200">
              <CardContent className="p-4 flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-amber-500 flex items-center justify-center">
                  <PiggyBank className="w-5 h-5 text-white" />
                </div>
                <div className="flex-1">
                  <p className="font-semibold text-amber-800">Économies potentielles</p>
                  <p className="text-sm text-amber-700">
                    Jusqu'à <strong>{totalPotentialSavings.toFixed(2)}€</strong> en achetant certains produits ailleurs
                  </p>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        )}

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-2 mb-4">
            <TabsTrigger value="products" className="text-sm gap-2">
              <ShoppingBag className="w-4 h-4" />
              Produits
            </TabsTrigger>
            <TabsTrigger value="stores" className="text-sm gap-2">
              <Store className="w-4 h-4" />
              Commerces
            </TabsTrigger>
          </TabsList>

          {/* Products Tab */}
          <TabsContent value="products" className="space-y-3">
            <div className="flex items-center justify-between mb-2">
              <h3 className="font-semibold text-charcoal flex items-center gap-2">
                <Trophy className="w-4 h-4 text-amber-500" />
                Top produits achetés
              </h3>
              <Badge variant="outline" className="text-xs">
                {stats.unique_products_count} produits
              </Badge>
            </div>

            {stats.products_ranking.slice(0, 20).map((product, index) => (
              <motion.div
                key={product.name}
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.03 }}
              >
                <Card className={`${product.cheaper_elsewhere ? 'border-amber-300 bg-amber-50/30' : ''}`}>
                  <CardContent className="p-3">
                    <div className="flex items-start gap-3">
                      {/* Rank */}
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                        index === 0 ? 'bg-amber-500 text-white' :
                        index === 1 ? 'bg-gray-400 text-white' :
                        index === 2 ? 'bg-amber-700 text-white' :
                        'bg-muted text-muted-foreground'
                      }`}>
                        {index + 1}
                      </div>

                      {/* Product Info */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-lg">{CATEGORY_EMOJIS[product.category] || "📦"}</span>
                          <p className="font-medium text-charcoal truncate">{product.name}</p>
                        </div>
                        <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground">
                          <span>{product.quantity_bought}x achetés</span>
                          <span>•</span>
                          <span>{product.avg_unit_price.toFixed(2)}€/unité</span>
                        </div>

                        {/* Progress bar for budget percentage */}
                        <div className="mt-2">
                          <div className="flex items-center justify-between text-xs mb-1">
                            <span className="text-muted-foreground">Part du budget</span>
                            <span className="font-medium text-primary">{product.percentage_of_budget}%</span>
                          </div>
                          <Progress value={product.percentage_of_budget} className="h-1.5" />
                        </div>

                        {/* Cheaper elsewhere alert */}
                        {product.cheaper_elsewhere && (
                          <Link to={`/stores/${product.cheaper_elsewhere.store_id}`}>
                            <div className="mt-2 p-2 bg-amber-100 rounded-lg flex items-center gap-2 hover:bg-amber-200 transition-colors">
                              <ArrowDown className="w-4 h-4 text-green-600" />
                              <div className="flex-1 text-xs">
                                <span className="text-amber-800">
                                  <strong>-{product.cheaper_elsewhere.savings_percent}%</strong> chez{" "}
                                  <span className="font-semibold">{product.cheaper_elsewhere.store_name}</span>
                                </span>
                                <span className="text-green-700 ml-2">
                                  ({product.cheaper_elsewhere.avg_price.toFixed(2)}€)
                                </span>
                              </div>
                              <ExternalLink className="w-3 h-3 text-amber-600" />
                            </div>
                          </Link>
                        )}

                        {/* Cheaper on Gusto Pro platform */}
                        {product.cheaper_on_platform && (
                          <Link to={`/business/${product.cheaper_on_platform.business_id}`}>
                            <div className="mt-2 p-2 bg-emerald-100 rounded-lg flex items-center gap-2 hover:bg-emerald-200 transition-colors" data-testid={`cheaper-platform-${product.name}`}>
                              <PiggyBank className="w-4 h-4 text-emerald-600" />
                              <div className="flex-1 text-xs">
                                <span className="text-emerald-800">
                                  <strong>-{product.cheaper_on_platform.savings_percent}%</strong> chez{" "}
                                  <span className="font-semibold">{product.cheaper_on_platform.store_name}</span>
                                </span>
                                <span className="text-emerald-700 ml-1">
                                  ({product.cheaper_on_platform.avg_price.toFixed(2)}€)
                                </span>
                                <Badge className="ml-1 bg-emerald-500 text-white text-[9px] px-1">Gusto Pro</Badge>
                              </div>
                              <ExternalLink className="w-3 h-3 text-emerald-600" />
                            </div>
                          </Link>
                        )}
                      </div>

                      {/* Total spent */}
                      <div className="text-right">
                        <p className="font-bold text-charcoal">{product.total_spent.toFixed(2)}€</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </TabsContent>

          {/* Stores Tab */}
          <TabsContent value="stores" className="space-y-3">
            <div className="flex items-center justify-between mb-2">
              <h3 className="font-semibold text-charcoal flex items-center gap-2">
                <MapPin className="w-4 h-4 text-primary" />
                Mes commerces
              </h3>
              <Badge variant="outline" className="text-xs">
                {stats.unique_stores_count} commerces
              </Badge>
            </div>

            {stats.store_stats.map((store, index) => {
              const percentOfTotal = (store.total_spent / stats.total_spent * 100);
              
              return (
                <motion.div
                  key={store.store_id}
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.05 }}
                >
                  <Link to={`/stores/${store.store_id}`}>
                    <Card className="hover:shadow-md transition-shadow cursor-pointer">
                      <CardContent className="p-4">
                        <div className="flex items-center gap-3">
                          {/* Rank */}
                          <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                            index === 0 ? 'bg-gradient-to-br from-amber-400 to-orange-500' :
                            index === 1 ? 'bg-gradient-to-br from-gray-300 to-gray-400' :
                            index === 2 ? 'bg-gradient-to-br from-amber-600 to-amber-700' :
                            'bg-muted'
                          }`}>
                            <Store className={`w-5 h-5 ${index < 3 ? 'text-white' : 'text-muted-foreground'}`} />
                          </div>

                          {/* Store Info */}
                          <div className="flex-1">
                            <p className="font-semibold text-charcoal">{store.store_name}</p>
                            <div className="flex items-center gap-3 text-xs text-muted-foreground mt-1">
                              <span>{store.tickets_count} tickets</span>
                              <span>•</span>
                              <span>~{store.avg_visits_per_month.toFixed(1)} visites/mois</span>
                            </div>
                          </div>

                          {/* Amount */}
                          <div className="text-right">
                            <p className="font-bold text-charcoal">{store.total_spent.toFixed(2)}€</p>
                            <p className="text-xs text-primary font-medium">{percentOfTotal.toFixed(1)}%</p>
                          </div>

                          <ExternalLink className="w-4 h-4 text-muted-foreground" />
                        </div>

                        {/* Progress bar */}
                        <div className="mt-3">
                          <Progress value={percentOfTotal} className="h-2" />
                        </div>
                      </CardContent>
                    </Card>
                  </Link>
                </motion.div>
              );
            })}

            {stats.store_stats.length === 0 && (
              <div className="text-center py-8">
                <Store className="w-12 h-12 text-muted-foreground/30 mx-auto mb-2" />
                <p className="text-muted-foreground">Aucun commerce identifié</p>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default PurchaseStatsPage;
